/**
 * A stay's care plan, and the edits that change it.
 *
 * adr/0023: an app opens on a SUBJECT. The board's URL names a stay, so this
 * hook takes a stay id and nothing else. Two things follow from that, and both
 * are this hook's job so no screen has to think about either:
 *
 *   - a care plan is the `care` category of the planning service's plan
 *     resource, found by its subject: `GET /plans/care?subject=stay/{id}&
 *     category=care`. A stay that has never been planned on has NO plan, and
 *     the first edit creates one — which is why the write path below may
 *     create before it replaces.
 *   - an edit carries the version the nurse decided against, and a stale one is
 *     refused rather than silently winning.
 *
 * ONE WRITE, THE WHOLE LIST. There are no per-item verbs on the wire any more.
 * What a nurse means is still an intent; it is applied to the item list here
 * and sent as one `PUT`, which the service compares against what it holds and
 * records item by item. The answer is the NEW plan with its new version, which
 * is written straight into the cache, so nothing re-reads the plan to learn
 * what its own write produced.
 *
 * A REPLACE IS NOT A PUBLISH, so every edit here publishes too. The published
 * version is the one in force; a plan replaced and left unpublished is a
 * proposal nobody acts on, and a board that silently produced one would show a
 * nurse a plan the ward is not working from.
 *
 * There is no care-plan stream in care-app to watch: a care plan is a plan in
 * the planner. The board reads the plan on mount and on window focus,
 * and a second nurse's edit surfaces as a refused command rather than a
 * silently overwritten one — which is the property that actually protects the
 * plan.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import {
  applyIntent,
  CARE_CATEGORY,
  CARE_SUBJECT_TYPE,
  intentTarget,
  type CarePlan,
  type CarePlanIntent,
} from '@/api/endpoints/carePlan';
import {
  createPlan,
  getPlan,
  listOccurrences,
  listPlans,
  OPEN_PLAN_STATUSES,
  publishPlan,
  replacePlanItems,
  type OccurrenceStatus,
} from '@/api/endpoints/plans';
import { findStayAcrossPatients, type StayResponse } from '@/api/endpoints/stays';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { createLogger } from '@/utils/logger';
import { randomUUID } from '@/lib/uuid';

const logger = createLogger('useCarePlan');

export const carePlanKeys = {
  all: ['carePlan'] as const,
  /** The stay behind a bare stay id — the patient the care-plan routes need. */
  stay: (stayId: string) => [...carePlanKeys.all, 'stay', stayId] as const,
  /** The fold itself. */
  plan: (stayId: string) => [...carePlanKeys.all, 'plan', stayId] as const,
  /** What was actually done, for one plan over one window of dates. */
  execution: (planId: string, from: string, to: string) =>
    [...carePlanKeys.all, 'execution', planId, from, to] as const,
};

/**
 * The week the board shows execution for: Monday to Sunday around `today`.
 *
 * A CARE PLAN IS A RECURRING WEEK — its items name a weekday, not a date — but
 * execution happens on dates. So a board showing "was this done?" has to mean
 * one concrete week, and the one a nurse means is the one she is in.
 *
 * The dates are the viewer's own, not a zone this app declares. The planner
 * places each occurrence in the category's declared zone and answers the date
 * it landed on; this only picks the window to ask about, and every row that
 * comes back carries the date it is really on.
 */
export function currentWeek(today: Date = new Date()): { from: string; to: string } {
  const day = (today.getDay() + 6) % 7; // Monday is 0, Sunday is 6.
  const monday = new Date(today);
  monday.setDate(today.getDate() - day);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(monday), to: iso(sunday) };
}

/**
 * What was actually done this week, per item.
 *
 * EXECUTION IS THE PLANNER'S, for every plan. Nothing here stores whether an
 * activity happened: the planner folds that out of the plan's own change log
 * and answers one row per item per date, and this keeps only the status.
 *
 * An item with no row is an item that does not occur this week. An item with a
 * row nobody acted on is `scheduled`, which the board draws as nothing — an
 * activity still ahead of the nurse is the normal case and must not look like
 * a state somebody put it in.
 *
 * A plan that is not in force has no occurrences at all and the planner
 * answers an empty page, so a draft board simply shows no execution.
 */
export function useCarePlanExecution(plan: CarePlan | null | undefined) {
  // NOT frozen with an empty dependency array. A ward workstation stays open
  // across midnight, and a window computed once at mount would keep asking for
  // last week after the date rolls — so a nurse coming on at 00:30 on Monday
  // would see Sunday's execution on Monday's board.
  const { from, to } = currentWeek();
  const query = useAuthenticatedQuery<Map<string, OccurrenceStatus>>({
    queryKey: carePlanKeys.execution(plan?.id ?? 'none', from, to),
    queryFn: async () => {
      const page = await listOccurrences(plan as CarePlan, from, to);
      const byItem = new Map<string, OccurrenceStatus>();
      for (const occurrence of page.items) {
        byItem.set(occurrence.item_id, occurrence.status);
      }
      return byItem;
    },
    enabled: !!plan,
  });
  // AN EMPTY MAP IS NOT AN ANSWER TO A FAILED READ. In flight and "nothing
  // recorded" do draw the same, and that is fine — both mean the nurse has
  // nothing to act on yet. A REFUSED read is different: an activity that was
  // cancelled or missed would draw as scheduled, which reads as care still
  // ahead of her rather than care that did not happen. So the error travels
  // and the board says so.
  return {
    execution: query.data ?? EMPTY_EXECUTION,
    error: query.error,
  };
}

const EMPTY_EXECUTION: ReadonlyMap<string, OccurrenceStatus> = new Map();

// The intent vocabulary lives beside the item shape it edits
// (api/endpoints/carePlan.ts), so the pure function that applies one and the
// type that describes one cannot drift. Re-exported here because every screen
// reaches the board through this hook.
export type { CarePlanIntent } from '@/api/endpoints/carePlan';

/**
 * The item this intent named is no longer in the plan: somebody removed it
 * between the nurse reading the board and pressing save.
 *
 * A distinct error type because it is the one conflict the board cannot resolve
 * on her behalf. Re-applying a retime to an item that is gone would either
 * recreate it under its old id or do nothing quietly; both are worse than
 * telling her what changed.
 *
 * It is raised HERE and not read off a server code: the item list is what the
 * intent is applied to, so the board knows the item is missing before it writes
 * anything, and the service never sees a write it would have to refuse.
 */
export class CarePlanInterventionGoneError extends Error {
  constructor(public interventionId: string) {
    super(`item ${interventionId} is no longer in this care plan`);
    this.name = 'CarePlanInterventionGoneError';
  }
}

/** True for a stale `If-Match`: the plan moved between the read and the write. */
function isPreconditionFailed(error: unknown): boolean {
  return error instanceof ApiError && error.status === 412;
}

/**
 * The plan a stay's board is open on, or null when nobody has planned yet.
 *
 * A list and not a get, because a plan is found by its SUBJECT: the board knows
 * the stay, and the plan's id is the planner's. An empty list is the answer for
 * a stay nobody has planned on, and it is a reason to create rather than an
 * error — which is why null travels rather than a throw.
 *
 * More than one plan on a stay is possible by the plan model, and the newest is
 * the one a board opens: a plan is created, worked on and finished, and the one
 * a nurse means is the one still in play. The service filters on status and
 * orders newest first, so the answer is the first element and there is no
 * status test here.
 */
async function findCarePlan(stayId: string): Promise<CarePlan | null> {
  const page = await listPlans(
    { type: CARE_SUBJECT_TYPE, id: stayId },
    CARE_CATEGORY,
    OPEN_PLAN_STATUSES,
  );
  if (page.items.length === 0) return null;
  return page.items[0] as CarePlan;
}

export interface UseCarePlanResult {
  /** The stay the board is open on, once resolved. */
  stay: StayResponse | undefined;
  /**
   * The plan. Undefined until the first read lands, and NULL once the read has
   * landed and found none — a stay nobody has planned on. The board draws an
   * empty week for the second and a loading state for the first, which are two
   * different things and were one while the plan was always served.
   */
  plan: CarePlan | null | undefined;
  isLoading: boolean;
  /** The read that failed, so the board can show what went wrong. */
  error: Error | null;
  /**
   * Apply one intent: replace the plan's items with the list that intent
   * produces, then publish, so what the ward acts on is what she just saw.
   *
   * Rejects with `CarePlanInterventionGoneError` when the item the nurse
   * targeted has been removed by somebody else, and with the underlying
   * `ApiError` for anything the caller cannot resolve. The board keeps its
   * editor open on a rejection and says what happened; nothing resolves
   * silently.
   */
  apply: (intent: CarePlanIntent) => Promise<CarePlan>;
}

/**
 * `stayId` may be null while the caller is still resolving which stay it is on
 * (a face whose patient frame has not settled). The queries stay disabled until
 * it is a real id.
 */
export function useCarePlan(stayId: string | null | undefined): UseCarePlanResult {
  const queryClient = useQueryClient();

  // The stay, and with it the patient the care-plan routes are nested under.
  // Cached separately from the fold: a stay's patient does not change, so this
  // read happens once per board even though the fold is re-read often.
  const stayQuery = useAuthenticatedQuery<StayResponse>({
    queryKey: carePlanKeys.stay(stayId ?? 'none'),
    queryFn: async () => {
      const stay = await findStayAcrossPatients(stayId as string);
      if (!stay) {
        // Not a null the caller has to interpret. There is no board to draw for
        // a stay that is not there, and an empty board would say the patient
        // has nothing planned.
        throw new Error(`no stay ${stayId as string} is visible to you`);
      }
      return stay;
    },
    enabled: !!stayId,
    refetchOnWindowFocus: false,
  });

  // The patient the plan cites in its attributes when the board creates one.
  // The plan itself is found by its stay, so this is needed only on the write
  // path; the read does not wait on it.
  const patientId = stayQuery.data?.patient;

  const planQuery = useAuthenticatedQuery<CarePlan | null>({
    queryKey: carePlanKeys.plan(stayId ?? 'none'),
    queryFn: () => findCarePlan(stayId as string),
    enabled: !!stayId,
    // Re-read when the nurse comes back to the tab. Until the stream is wired
    // this is what picks up a colleague's edit without her doing anything, and
    // it stays useful afterwards as the catch-up after a sleeping laptop.
    refetchOnWindowFocus: true,
  });

  const apply = useCallback(
    async (intent: CarePlanIntent): Promise<CarePlan> => {
      if (!stayId) {
        throw new Error('the care plan is not open on a stay yet');
      }
      const held = queryClient.getQueryData<CarePlan | null>(carePlanKeys.plan(stayId));
      if (held === undefined) {
        throw new Error('the care plan has not been read yet');
      }
      // ONE KEY PER VERB. The planner records a key against the plan's stream
      // and answers any later call carrying it with what the first call
      // answered, so a replace and a publish sharing a key would publish
      // nothing: the publish would be answered with the replace.

      // A stay nobody has planned on has no plan yet, and the first edit makes
      // one. The create carries the patient the category requires in its
      // attributes — a citation, not a second home for the fact, which is why
      // the board cannot make one before the stay has resolved.
      let plan = held;
      if (plan === null) {
        if (!patientId) {
          throw new Error('the stay this board is on has not resolved its patient yet');
        }
        logger.info('Creating the first care plan for this stay', { stayId });
        plan = (await createPlan({
          subject: { type: CARE_SUBJECT_TYPE, id: stayId },
          category: CARE_CATEGORY,
          attributes: { patient: `patients/${patientId}` },
          idempotencyKey: randomUUID(),
        })) as CarePlan;
      }

      // Applying the intent BEFORE writing is what makes a missing item her
      // answer rather than the server's refusal: the board knows the item is
      // gone without asking, and nothing is written that would have to be
      // undone.
      const write = async (against: CarePlan): Promise<CarePlan> => {
        const items = applyIntent(against.items, intent);
        if (items === null) {
          throw new CarePlanInterventionGoneError(intentTarget(intent) ?? '');
        }
        // A CarePlan IS a PlanRef — it carries its category and its id — so the
        // address travels as the plan itself rather than being taken apart.
        const replaced = (await replacePlanItems(
          against,
          against.version,
          items,
          randomUUID(),
        )) as CarePlan;
        // The replace is a proposal until it is published, and a nurse editing
        // a board is stating the plan, not proposing one. Publishing here is
        // what keeps what the ward acts on equal to what she just saw.
        return (await publishPlan(replaced, replaced.version, randomUUID())) as CarePlan;
      };

      try {
        const written = await write(plan);
        queryClient.setQueryData(carePlanKeys.plan(stayId), written);
        // Publishing puts a new version in force, so the occurrences folded
        // from it are no longer the ones that were read. Without this the
        // board keeps last version's execution against this version's items.
        void queryClient.invalidateQueries({ queryKey: [...carePlanKeys.all, 'execution'] });
        return written;
      } catch (error) {
        if (!isPreconditionFailed(error)) throw error;

        // 412: somebody else changed the plan between her read and her save.
        // Re-read it, and re-apply what she meant against what is there now.
        logger.info('Care plan moved under an edit; re-reading and re-applying', {
          stayId,
          verb: intent.verb,
          decidedAgainst: plan.version,
        });
        const fresh = (await queryClient.fetchQuery({
          queryKey: carePlanKeys.plan(stayId),
          queryFn: () => getPlan(plan),
        })) as CarePlan;

        // Exactly one re-application, never a loop. A second refusal in the
        // same breath means the plan is moving continuously, and a board that
        // keeps re-applying her intent is applying it to a plan she has never
        // seen. She sees the conflict instead, on a board that now shows what
        // is actually there.
        const written = await write(fresh);
        queryClient.setQueryData(carePlanKeys.plan(stayId), written);
        return written;
      }
    },
    [queryClient, stayId, patientId],
  );

  return useMemo(
    () => ({
      stay: stayQuery.data,
      plan: planQuery.data,
      isLoading: stayQuery.isLoading || planQuery.isLoading,
      error: stayQuery.error ?? planQuery.error,
      apply,
    }),
    [
      stayQuery.data,
      stayQuery.isLoading,
      stayQuery.error,
      planQuery.data,
      planQuery.isLoading,
      planQuery.error,
      apply,
    ],
  );
}
